import { type FC, type ReactNode } from 'react';
import {
    vettedSeller,
    goldSeller,
    platinumSeller,
    diamondSeller,
    recognizedSeller,
    recognizedDescription,
    goldTooltip,
    platinumTooltip,
    diamondTooltip,
    vettedTooltip,
} from '../../messages/pdpDistinctionsMessages.jsx';
import { graphql, useFragment } from 'react-relay';

import Recognition from 'dibs-icons/exports/Recognition.ts';
import Diamond from 'dibs-icons/exports/Diamond.ts';
import Medal from 'dibs-icons/exports/Medal.ts';
import Vetting from 'dibs-icons/exports/Vetting.ts';

// styles
import styles from './PdpSharedSellerDistinction.scss';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

// types
import { type PdpSharedSellerDistinction_seller$key as Seller } from './__generated__/PdpSharedSellerDistinction_seller.graphql.ts';

type Props = {
    seller: Seller;
    isCompact?: boolean;
};

type CreateDealerDistinctionProps = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
};

const createDealerDistinction = ({
    icon,
    title,
    description,
}: CreateDealerDistinctionProps): ReactNode => (
    <>
        <div className={classNames(styles.distinction, dibsCss.flex, dibsCss.itemsCenter)}>
            {icon}
            <span className={styles.title}>{title}</span>
        </div>
        <div className={classNames(styles.description, dibsCss.textSatan)}>{description}</div>
    </>
);

const sellerFragment = graphql`
    fragment PdpSharedSellerDistinction_seller on Seller {
        isDistinguished
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
    }
`;

export const PdpSharedSellerDistinction: FC<Props> = ({ seller: sellerRef, isCompact = false }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const sellerTiers = seller?.sellerTiers;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const isDistinguished = seller?.isDistinguished;
    const sellerTierDataFound = sellerTiers?.sellerTierDataNotFound;
    const isGoldSeller = !sellerTierDataFound && sellerTiers?.isGoldTier;
    const isPlatinumSeller = !sellerTierDataFound && sellerTiers?.isPlatinumTier;
    const distinctions = [];

    if (isDistinguished) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classNames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillSuccesssecondary
                        )}
                    >
                        <Recognition variation="20" />
                    </span>
                ),
                title: recognizedSeller,
                description: recognizedDescription,
            })
        );
    }

    if (isGoldSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classNames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillBuyersecondary
                        )}
                    >
                        <Medal variation="20" />
                    </span>
                ),
                title: goldSeller,
                description: goldTooltip,
            })
        );
    } else if (isPlatinumSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classNames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillMedalplatinum
                        )}
                    >
                        <Medal variation="20" />
                    </span>
                ),
                title: platinumSeller,
                description: platinumTooltip,
            })
        );
    } else if (isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(dibsCss.shrink0, dibsCss.grow0, dibsCss.mrXsmall)}>
                        <Diamond variation="20" />
                    </span>
                ),
                title: diamondSeller,
                description: diamondTooltip,
            })
        );
    } else if (!distinctions.length) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classNames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillShagreen800
                        )}
                    >
                        <Vetting variation="20" />
                    </span>
                ),
                title: vettedSeller,
                description: vettedTooltip,
            })
        );
    }

    return (
        <>
            {distinctions.map((distinction, index) => (
                <div
                    className={classNames({
                        [dibsCss.mbSmaller]: isCompact,
                        [dibsCss.mbXsmall]: !isCompact,
                    })}
                    key={`distinction-${index}`}
                >
                    {distinction}
                </div>
            ))}
        </>
    );
};
